import { GenericRequestBody, GenericResponse } from '@omnea-digital/toolbox'
import createClient from 'openapi-fetch'
import { withErrorCapture } from '@omnea-app/packages/universal/general/observability/withErrorCapture'
import { ServiceUrls } from '@omnea-app/packages/universal/general/serviceUrls'
import {
  openApiFetchAuthMiddleware,
  type AuthorizationMiddlewareOptions,
} from '@omnea-app/packages/universal/open-api-fetch/openApiFetchAuthMiddleware'
import { paths } from './rfxGeneratedTypes'

export type ScoresByOrgSupplierId =
  paths['/tasks/{taskId}/responses-and-scores']['get']['responses']['200']['content']['application/json']['scoresByOrgSupplierId']

export type AiScoresByOrgSupplierId =
  paths['/tasks/{taskId}/responses-and-scores']['get']['responses']['200']['content']['application/json']['aiScoresByOrgSupplierId']

export type AiScore = AiScoresByOrgSupplierId[string][string]

export type AiConfidenceRating = AiScore['confidence']['rating']

export type UpdateRfx = NonNullable<paths['/{rfxId}/update-state']['post']['requestBody']>['content']['application/json']

export type RfxDetail = paths['/{rfxId}']['get']['responses']['200']['content']['application/json']

export type RfxScoringProgress = paths['/{rfxId}/scoring-progress']['get']['responses']['200']['content']['application/json']

export type RfxEvaluation = paths['/{rfxId}/evaluation']['get']['responses']['200']['content']['application/json']

export type RfxEvaluationRow = RfxEvaluation['rows'][number]

export type RfxAiScoringStatus = paths['/{rfxId}/ai-scoring-status']['get']['responses']['200']['content']['application/json']

export type RfxPricingAnalysisStatus =
  paths['/{rfxId}/pricing-analysis/{formTemplateId}/status']['get']['responses']['200']['content']['application/json']

export type RfxFormTemplateInsightsStatus =
  paths['/{rfxId}/form-template-insights/status']['get']['responses']['200']['content']['application/json']

export type RfxFormGenerationStatusResponse =
  paths['/{rfxId}/form-generation-status']['get']['responses']['200']['content']['application/json']

export type RfxFormTemplateInsight =
  paths['/{rfxId}/form-template-insights']['get']['responses']['200']['content']['application/json']['insights'][number]

export type RfxSupplierStatus = NonNullable<
  paths['/{rfxId}/rfx-supplier/{rfxSupplierId}/set-status']['post']['requestBody']
>['content']['application/json']['status']
export type RfxStatus = RfxDetail['status']

export type UpdateRfxSettings = NonNullable<paths['/{rfxId}/settings']['post']['requestBody']>['content']['application/json']

export type UpdateQnaStatusBody = NonNullable<paths['/{rfxId}/qna/status']['post']['requestBody']>['content']['application/json']

export type ExecutiveSummaryFormat = NonNullable<
  paths['/{rfxId}/executive-summary']['post']['requestBody']
>['content']['application/json']['format']

export type ListQnaItemsResponse = paths['/{rfxId}/qna/items']['post']['responses']['200']['content']['application/json']
export type QnaItemRow = ListQnaItemsResponse['items'][number]

export type GetQnaDetailResponse =
  paths['/{rfxId}/qna/items/{qnaItemId}']['get']['responses']['200']['content']['application/json']

export type SaveQnaAnswerBody = NonNullable<
  paths['/{rfxId}/qna/items/{qnaItemId}/answer']['post']['requestBody']
>['content']['application/json']

export type SaveQnaAnswerResponse =
  paths['/{rfxId}/qna/items/{qnaItemId}/answer']['post']['responses']['200']['content']['application/json']

export type UpdateQnaItemsStatusBody = NonNullable<
  paths['/{rfxId}/qna/items/status']['post']['requestBody']
>['content']['application/json']

export type GroupQnaItemsBody = NonNullable<
  paths['/{rfxId}/qna/items/group']['post']['requestBody']
>['content']['application/json']

export type GroupQnaItemsResponse = paths['/{rfxId}/qna/items/group']['post']['responses']['200']['content']['application/json']

export type UngroupQnaQuestionBody = NonNullable<
  paths['/{rfxId}/qna/items/{qnaItemId}/ungroup-question']['post']['requestBody']
>['content']['application/json']

export type UngroupQnaQuestionResponse =
  paths['/{rfxId}/qna/items/{qnaItemId}/ungroup-question']['post']['responses']['200']['content']['application/json']

export type RfxResponse<T extends keyof paths, T2 extends keyof paths[T]> = GenericResponse<paths, T, T2>
export type RfxApiRequestBody<TPath extends keyof paths, TMethod extends keyof paths[TPath]> = GenericRequestBody<
  paths,
  TPath,
  TMethod
>

type Options = {
  authorization: AuthorizationMiddlewareOptions
}

export class RfxHttpClient {
  GET
  POST
  constructor({ authorization }: Options) {
    const client = createClient<paths>({ baseUrl: ServiceUrls.RFX })

    client.use(openApiFetchAuthMiddleware(authorization))

    this.GET = withErrorCapture({ method: client.GET })
    this.POST = withErrorCapture({ method: client.POST })
  }
}
